import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { ApisauceInstance, create } from 'apisauce';
import firebase from 'firebase/compat/app';

import AccountContext from 'contexts/AccountContext';
import BonusProgramPeriodsProvider from 'contextProviders/BonusProgramPeriodsProvider';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import GqlClientContext from 'contexts/GqlClientContext';
import EntityDetailsContext from 'screens/EntityDetailsShow/EntityDetailsContext';
import FlexCentered from 'components/Common/FlexCentered';
import Loading from 'components/Loading';
import Typography from 'kingpin/atoms/Typography';
import { getRequestHeaders } from 'services/buildCloudFunctionClient';

import BonusOverviewProvider, {
  BonusOverviewContext,
} from '../../providers/BonusOverviewProvider';
import fetchProgramPeriodDocuments from './fetchProgramPeriodDocuments';
import fetchProgramsWithOngoing from './fetchProgramsWithOngoing';
import getBonusRecordDriverType from './getBonusRecordDriverType';
import getEmbedProgramsByConfigId, {
  EmbedProgram,
} from './getEmbedProgramsByConfigId';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

import env from 'env/.public-env.json';

export const DRIVER_APP_EMBED_ORIGIN: string =
  ((env as Record<string, unknown>)
    .REACT_APP_DRIVER_REWARD_DETAIL_URL as string) || '';

export const DRIVER_APP_EMBED_PREVIEW_ORIGIN: string =
  ((env as Record<string, unknown>)
    .REACT_APP_DRIVER_REWARD_DETAIL_PREVIEW_URL as string) || '';

// Path the embed is served under, appended to the origin for the iframe src.
// Defaults to '/embed' (the deployed layout); set REACT_APP_DRIVER_REWARD_DETAIL_PATH
// to '' to load an embed served at the origin root (e.g. a local dev server).
export const DRIVER_APP_EMBED_PATH: string =
  ((env as Record<string, unknown>)
    .REACT_APP_DRIVER_REWARD_DETAIL_PATH as string) ?? '/embed';

// The programSlug that tells the embed to land on the home screen (which lists
// every program) rather than a single program's detail. Any other slug value
// opens a program detail; the embed identifies the program from the injected
// bootstrap bonusDocument, so the host doesn't map programs to slugs itself.
export const HOME_PROGRAM_SLUG = 'home';

const DRIVER_API_BASE_URL = 'https://drivers.fleetops.com/api';

const ALLOWED_RESOURCE_PATH_PREFIXES = [
  '/v2/resources/',
  '/v3/resources/',
] as const;

const isAllowedResourcePath = (path: string): boolean =>
  ALLOWED_RESOURCE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));

interface ResourceRequestMessage {
  type: 'RESOURCE_REQUEST';
  requestId: string;
  method: 'GET';
  path: string;
  params: Record<string, unknown>;
}

interface PeriodsRequestMessage {
  type: 'PERIODS_REQUEST';
  requestId: string;
  carrierSlug: string;
  driverId: string;
  // The program config id — stable across periods.
  bonusConfigDocId: string;
}

interface ProgramsRequestMessage {
  type: 'PROGRAMS_REQUEST';
  requestId: string;
  carrierSlug: string;
  driverId: string;
}

// On-demand lookup of a driver's roster `driverType` (e.g. "OE") for the OE
// toggle. The value lives on the raw bonus record, not the reward output, so the
// embed sends the record it's showing (id + program config id for dataset
// resolution + the period dates for search scoping) and the host resolves it via
// search2. Kept separate from EMBED_INIT so the toggle isn't coupled to init
// timing and the lookup only runs when the embed actually needs it.
interface DriverTypeRequestMessage {
  type: 'DRIVER_TYPE_REQUEST';
  requestId: string;
  bonusDatasetRecordId: string;
  // The program config id (== bonusConfigDocId), used to resolve the program's
  // output dataset.
  bonusConfigDocId: string;
  startDate: string;
  endDate: string;
}

// ---------------------------------------------------------------------------
// EmbedFrame — the iframe + postMessage handshake
// ---------------------------------------------------------------------------

export const buildInitPayload = ({
  accountId,
  carrierSlug,
  programSlug,
  scoreId,
  driverId,
  bonusDocument,
  periodStatus,
}: {
  accountId: string;
  carrierSlug: string;
  programSlug: string;
  scoreId: string;
  driverId: string;
  bonusDocument: BonusTypes.Rewards.AnyDriverRewardsOutput;
  periodStatus: BonusTypes.PeriodStatus;
}) => ({
  type: 'EMBED_INIT',
  accountId,
  carrierSlug,
  programSlug,
  scoreId,
  driverId,
  // Carry `id` (== bonusDatasetRecordId) and `periodStatus` on the doc so the
  // embed reads them uniformly across EMBED_INIT / PROGRAMS_RESPONSE /
  // PERIODS_RESPONSE. The top-level `scoreId` / `periodStatus` stay as the
  // launch entry selector + status. The launch doc is already a single (week)
  // record; the embed reads the sub-period weeks from the expanded
  // PERIODS_RESPONSE window, not from here.
  bonusDocument: {
    ...bonusDocument,
    id: bonusDocument.bonusDatasetRecordId,
    periodStatus,
  },
  periodStatus,
});

type InitPayload = ReturnType<typeof buildInitPayload>;

export const EmbedFrame = ({
  accountId,
  carrierSlug,
  programSlug,
  bonusDocument,
  periodStatus,
  gqlClient,
  gatewayApi,
  programsByConfigId,
  origin = DRIVER_APP_EMBED_ORIGIN,
}: {
  accountId: string;
  carrierSlug: string;
  programSlug: string;
  bonusDocument: BonusTypes.Rewards.AnyDriverRewardsOutput;
  periodStatus: BonusTypes.PeriodStatus;
  // Apollo client (GqlClientContext) used to answer DRIVER_TYPE_REQUEST — the
  // roster `driverType` lives on the raw bonus record, reachable via search2.
  gqlClient: ApolloClient<NormalizedCacheObject>;
  // The API-gateway client (CloudFunctionClientContext) used to fetch sibling
  // period reward outputs — distinct from the drivers-API `resourceApi` below.
  gatewayApi: ApisauceInstance;
  // Every bonus program on the account (name + non-archived periods), keyed by
  // program config id (== bonusConfigDocId). Powers both handshakes:
  // PERIODS_REQUEST (one program's full multi-year period history) and
  // PROGRAMS_REQUEST (every program + its ongoing period). The handler must be
  // able to resolve any of the account's programs — not just the embedded one
  // — so a switched-to program's history can be requested too.
  programsByConfigId: {
    [bonusConfigDocId: string]: EmbedProgram;
  };
  // The embed origin to load and to trust for postMessage. Defaults to the
  // live embed; the Live/Preview menu passes the preview origin here.
  origin?: string;
}) => {
  const resourceApi = useMemo(() => {
    const api = create({
      baseURL: DRIVER_API_BASE_URL,
      headers: {
        accept: 'application/json',
        'x-account-id': accountId,
      },
    });
    api.addAsyncRequestTransform((request) => async () => {
      const user = firebase.auth().currentUser;
      if (user) {
        const token = await user.getIdToken();
        getRequestHeaders(request)['x-client-authentication'] =
          `Bearer ${token}`;
      }
    });
    // The drivers API requires lowercase header names, but axios keeps the
    // casing of its default headers (e.g. Accept) when ours differ only by case
    api.axiosInstance.interceptors.request.use((config) => {
      Object.entries(config.headers).forEach(([key, value]) => {
        const lowered = key.toLowerCase();
        if (lowered !== key) {
          config.headers.delete(key);
          config.headers.set(lowered, value);
        }
      });
      return config;
    });
    return api;
  }, [accountId]);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const readyRef = useRef(false);
  const dataRef = useRef<InitPayload | null>(null);
  const apiRef = useRef(resourceApi);
  const gatewayApiRef = useRef(gatewayApi);
  const gqlClientRef = useRef(gqlClient);
  const programsByConfigIdRef = useRef(programsByConfigId);
  const accountIdRef = useRef(accountId);
  const originRef = useRef(origin);

  const payload = useMemo(
    () =>
      buildInitPayload({
        accountId,
        carrierSlug,
        programSlug,
        scoreId: bonusDocument.bonusDatasetRecordId,
        driverId: bonusDocument.driver,
        bonusDocument,
        periodStatus,
      }),
    [accountId, carrierSlug, programSlug, bonusDocument, periodStatus],
  );

  // Keep refs in sync so the message handler always uses the latest values
  useEffect(() => {
    dataRef.current = payload;
  }, [payload]);
  useEffect(() => {
    apiRef.current = resourceApi;
  }, [resourceApi]);
  useEffect(() => {
    gatewayApiRef.current = gatewayApi;
  }, [gatewayApi]);
  useEffect(() => {
    gqlClientRef.current = gqlClient;
  }, [gqlClient]);
  useEffect(() => {
    programsByConfigIdRef.current = programsByConfigId;
  }, [programsByConfigId]);
  useEffect(() => {
    accountIdRef.current = accountId;
  }, [accountId]);
  useEffect(() => {
    originRef.current = origin;
  }, [origin]);

  // Set up the message listener once on mount
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== originRef.current) return;
      if (!event.source || event.source !== iframeRef.current?.contentWindow)
        return;

      if (event.data?.type === 'REWARD_DETAIL_READY') {
        readyRef.current = true;
        if (dataRef.current) {
          console.log('[BonusAppEmbed] posting EMBED_INIT (on ready)', {
            carrierSlug: dataRef.current.carrierSlug,
            programSlug: dataRef.current.programSlug,
            scoreId: dataRef.current.scoreId,
            driverId: dataRef.current.driverId,
          });
          (event.source as Window).postMessage(
            dataRef.current,
            originRef.current,
          );
        }
      }
      // REWARD_DETAIL_HEIGHT intentionally ignored — fixed phone viewport

      if (event.data?.type === 'RESOURCE_REQUEST') {
        const { requestId, method, path, params } =
          event.data as ResourceRequestMessage;

        if (!requestId || !path) return;
        if (method !== 'GET') return;

        const replyTo = event.source as Window;

        if (!isAllowedResourcePath(path)) {
          console.warn('[BonusAppEmbed] blocked disallowed path:', path);
          replyTo.postMessage(
            {
              type: 'RESOURCE_RESPONSE',
              requestId,
              ok: false,
              status: 403,
              data: { error: 'Path not allowed' },
            },
            originRef.current,
          );
          return;
        }

        const currentApi = apiRef.current;
        if (!currentApi) return;

        currentApi.get(path, params).then((response) => {
          replyTo.postMessage(
            {
              type: 'RESOURCE_RESPONSE',
              requestId,
              ok: response.ok,
              status: response.status,
              data: response.data,
            },
            originRef.current,
          );
        });
      }

      if (event.data?.type === 'PERIODS_REQUEST') {
        const { requestId, driverId, bonusConfigDocId } =
          event.data as PeriodsRequestMessage;

        if (!requestId) return;

        const replyTo = event.source as Window;

        const postResponse = (
          bonusDocuments: BonusTypes.Rewards.AnyDriverRewardsOutput[],
        ) =>
          replyTo.postMessage(
            { type: 'PERIODS_RESPONSE', requestId, ok: true, bonusDocuments },
            originRef.current,
          );

        // Resolve the requested program. Any program on the account is
        // answerable (so a switched-to program's history can be requested too);
        // a config id the account doesn't have gets an empty (but successful)
        // response so the embed doesn't wait.
        const program = programsByConfigIdRef.current[bonusConfigDocId];
        if (!program) {
          postResponse([]);
          return;
        }

        console.log('[BonusAppEmbed] handling PERIODS_REQUEST', {
          bonusConfigDocId,
          driverId,
        });

        // Full multi-year history for the program — no year scoping.
        fetchProgramPeriodDocuments({
          api: gatewayApiRef.current,
          accountId: accountIdRef.current,
          driverId,
          bonusConfigDocId,
          periods: program.periods,
        }).then((bonusDocuments) => {
          console.log('[BonusAppEmbed] posting PERIODS_RESPONSE', {
            requestId,
            count: bonusDocuments.length,
          });
          postResponse(bonusDocuments);
        });
      }

      if (event.data?.type === 'PROGRAMS_REQUEST') {
        const { requestId, driverId } = event.data as ProgramsRequestMessage;

        if (!requestId) return;

        const replyTo = event.source as Window;

        // Return an entry for every program the driver is featured in for its
        // ongoing period — the backend's active period, falling back to date
        // containment only when none is active. `today` powers that fallback.
        const today = new Date().toISOString().slice(0, 10);

        fetchProgramsWithOngoing({
          api: gatewayApiRef.current,
          driverId,
          programsByConfigId: programsByConfigIdRef.current,
          today,
        }).then((programs) => {
          console.log('[BonusAppEmbed] posting PROGRAMS_RESPONSE', {
            requestId,
            count: programs.length,
          });
          replyTo.postMessage(
            { type: 'PROGRAMS_RESPONSE', requestId, ok: true, programs },
            originRef.current,
          );
        });
      }

      if (event.data?.type === 'DRIVER_TYPE_REQUEST') {
        const {
          requestId,
          bonusDatasetRecordId,
          bonusConfigDocId,
          startDate,
          endDate,
        } = event.data as DriverTypeRequestMessage;

        if (!requestId) return;

        const replyTo = event.source as Window;

        const postResponse = (driverType: string | undefined) =>
          replyTo.postMessage(
            { type: 'DRIVER_TYPE_RESPONSE', requestId, ok: true, driverType },
            originRef.current,
          );

        // Resolve the program's output dataset from its config id. A record/
        // config we can't resolve still gets a successful (empty) response so the
        // embed doesn't wait — it just keeps the OE toggle hidden.
        const dataType =
          programsByConfigIdRef.current[bonusConfigDocId]?.dataset;
        if (!dataType || !bonusDatasetRecordId || !startDate || !endDate) {
          postResponse(undefined);
          return;
        }

        getBonusRecordDriverType({
          client: gqlClientRef.current,
          dataType,
          bonusDatasetRecordId,
          startDate,
          endDate,
        }).then((driverType) => {
          console.log('[BonusAppEmbed] posting DRIVER_TYPE_RESPONSE', {
            requestId,
            driverType,
          });
          postResponse(driverType);
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Re-push EMBED_INIT whenever the payload changes (e.g. period switch)
  useEffect(() => {
    if (!readyRef.current || !iframeRef.current?.contentWindow) return;
    console.log('[BonusAppEmbed] re-posting EMBED_INIT (payload change)', {
      carrierSlug: payload.carrierSlug,
      programSlug: payload.programSlug,
      scoreId: payload.scoreId,
      driverId: payload.driverId,
    });
    iframeRef.current.contentWindow.postMessage(payload, originRef.current);
  }, [payload]);

  return (
    <iframe
      ref={iframeRef}
      src={`${origin}${DRIVER_APP_EMBED_PATH}`}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        display: 'block',
      }}
      title="Bonus App"
    />
  );
};

// ---------------------------------------------------------------------------
// Gate — resolves driver from entity context, kept for any legacy usages
// ---------------------------------------------------------------------------

const BonusAppEmbedContainer = ({
  program,
}: {
  program: BonusTypes.BonusProgram;
}) => {
  const { id, driverAppEmbedCarrierSlug, driverAppEmbedProgramSlug } =
    useContext(AccountContext);
  const { isLoading, bonusOverview } = useContext(BonusOverviewContext);
  const { api } = useContext(CloudFunctionClientContext);
  const { client } = useContext(GqlClientContext);
  const { bonusPrograms, bonusPeriodsLookup } = useContext(
    DatasetDefinitionsContext,
  );

  // Every program on the account (name + non-archived periods + output dataset),
  // keyed by config id, so the embed handshakes can resolve any program — the
  // launch one for PERIODS, all of them for PROGRAMS (home cards), and the
  // output dataset for DRIVER_TYPE.
  const programsByConfigId = useMemo(
    () => getEmbedProgramsByConfigId({ bonusPrograms, bonusPeriodsLookup }),
    [bonusPrograms, bonusPeriodsLookup],
  );

  const programSlug = driverAppEmbedProgramSlug ?? 'bonus';

  if (!driverAppEmbedCarrierSlug) {
    return (
      <FlexCentered>
        <Typography.Body type="Body 13">
          Driver app embed not configured.
        </Typography.Body>
      </FlexCentered>
    );
  }

  if (isLoading) {
    return (
      <FlexCentered>
        <Loading />
      </FlexCentered>
    );
  }

  if (!bonusOverview) {
    return (
      <FlexCentered>
        <Typography.Header type="H3">
          Bonus information not found
        </Typography.Header>
      </FlexCentered>
    );
  }

  return (
    <EmbedFrame
      accountId={id}
      carrierSlug={driverAppEmbedCarrierSlug}
      programSlug={programSlug}
      bonusDocument={bonusOverview.actualRewardOutput}
      periodStatus={bonusOverview.bonusPeriod.status}
      gqlClient={client}
      gatewayApi={api}
      programsByConfigId={programsByConfigId}
    />
  );
};

// ---------------------------------------------------------------------------
// Gate — mirrors DriverBonusOverview: resolves driver from entity context
// ---------------------------------------------------------------------------

const Gate = ({ program }: { program: BonusTypes.BonusProgram }) => {
  const entityDetailsContext = useContext(EntityDetailsContext);

  const driver = useMemo(() => {
    if (!entityDetailsContext?.selectedEntity) {
      return undefined;
    }
    if (entityDetailsContext.selectedEntity.type.primaryField !== 'driver') {
      return undefined;
    }
    return entityDetailsContext.selectedEntity.fieldValue;
  }, [entityDetailsContext]);

  if (!driver) {
    return (
      <FlexCentered>
        <Typography.Header type="H3">
          This screen is only compatible with the driver entity
        </Typography.Header>
      </FlexCentered>
    );
  }

  return (
    <BonusProgramPeriodsProvider programId={program.id}>
      <BonusOverviewProvider
        bonusProgramId={program.id}
        driver={driver}
        variant={'overview'}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <BonusAppEmbedContainer program={program} />
        </div>
      </BonusOverviewProvider>
    </BonusProgramPeriodsProvider>
  );
};

export default Gate;
